import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, NavLink } from 'react-router-dom';
import { map } from 'lodash';
import { IndexType, MenuItem } from '@/type';
import { LanguageSwitch, NotificationBanner } from '@/modules/components';
import { useInteractionState } from '@/hooks';
import { toast } from 'react-toastify';

type Props = {
  menuItems: MenuItem[];
};

export const NavigationComponent = ({ menuItems }: Props) => {
  const { orders, management } = useInteractionState();

  const [activeIndex, setActiveIndex] = useState<number | undefined>(0);
  const [displayMenu, setDisplayMenu] = useState(false);

  const closeMenu = (anchorName?: string, index?: number) => {
    setDisplayMenu((displayMenu: boolean) => !displayMenu);
    if (anchorName) {
      const anchorElement = document.getElementById(anchorName);
      if (anchorElement) {
        setActiveIndex(index);
        anchorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  const menuFile =
    management?.menuDocument?.entity_type === IndexType.ADDITIONAL
      ? management.menuDocument
      : undefined;

  const openMenuFile = async () => {
    const menuFilePath = !import.meta.env.VITE_DOCUMENT_BLOB_BACKUP
      ? menuFile?.path
      : import.meta.env.VITE_DOCUMENT_BLOB_BACKUP === 'supabase'
      ? menuFile?.path_supabase
      : menuFile?.path_other_backup ?? '';
    if (menuFilePath) {
      const response = await fetch(menuFilePath, {
        headers: { Accept: 'application/pdf' },
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } else {
      toast(
        <NotificationBanner
          description={'No Menu file found!'}
          buttonText={'Close'}
          buttonAction={() => toast.dismiss()}
        />,
        {
          position: 'top-center',
          type: 'error',
        }
      );
    }
  };

  const items = map(menuItems, (it: MenuItem, key: number) => {
    const navElement = (
      <NavLink
        className={() =>
          key === activeIndex ? 'navactive color_animation' : 'color_animation'
        }
        to={it.target}
        onClick={
          it.navigateToMenu
            ? () => openMenuFile()
            : () => closeMenu(it.link_path, key)
        }
      >
        <FormattedMessage id={it.key} />
      </NavLink>
    );
    const cartElement = (
      <span className="color_animation">
        <Link style={{ fontSize: '24px', margin: '0 12px' }} to="/cart">
          <span className="glyphicon glyphicon-shopping-cart"></span>
          <span className="badge" style={{ fontSize: '18px' }}>
            {orders?.length ?? 0}
          </span>
        </Link>
      </span>
    );
    return (
      <React.Fragment key={key}>
        {it.show && <li>{it.navigateToCart ? cartElement : navElement}</li>}
      </React.Fragment>
    );
  });

  return (
    <nav className="navbar navbar-default navbar-fixed-top">
      <div className="container">
        <div className="row">
          <div className="navbar-header">
            <button
              type="button"
              className="navbar-toggle collapsed"
              onClick={() => closeMenu()}
            >
              <span className="sr-only" />
              <span className="icon-bar" />
              <span className="icon-bar" />
              <span className="icon-bar" />
            </button>
            <p className="navbar-brand">
              <FormattedMessage id="app.restaurant.name" />
            </p>
          </div>
          <div
            className="collapse navbar-collapse"
            style={{ display: displayMenu ? 'block' : '' }}
          >
            <ul className="nav main-nav">
              {items}
              <li key={items.length + 1} className="color_animation">
                <LanguageSwitch />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};
